<script lang="ts">
    import { createAddress, getAddress, getAddresses, updateAddress } from "../../api"
    import MiniLoader from "../widgets/MiniLoader.svelte"
    import Table from "../widgets/Table.svelte"
    import { tooltip, getFilterForAddressSearch, requireLogin } from "./../../helper"
    import { debounce } from "lodash"
    import hash from "object-hash"
    import { mdiFileDocumentEdit, mdiDelete, mdiDomain, mdiAccount, mdiPlus } from "@mdi/js"
    import Icon from "./../widgets/Icon.svelte"
    import Modal from "../widgets/Modal.svelte"
    import AddressTypeField from "../widgets/fields/AddressTypeField.svelte"
    import ConfirmDeleteAddressDialog from "./addresses/ConfirmDeleteAddress.svelte"
    import AddressForm from "../widgets/AddressForm.svelte"
    import { login, newNotification } from "../../store"
    import AssignedPersons from "./addresses/AssignedPersons.svelte"
    import { emptyAddressObj } from "../../helperObjects.js"
    import TextField from "../widgets/fields/TextField.svelte"
    import { validateAndSaveAddress } from "../../functions/address/address"
    import { _ } from "svelte-i18n"

    export let lang = "de"
    const gridBreakPoint = 1300
    let innerWidth = window.innerWidth
    let outerHeight: number
    let innerHeight: number
    let scrollY: number
    requireLogin()
    const emptyAddress = JSON.parse(JSON.stringify(emptyAddressObj))

    let search: string = ""
    $: if (search) filter = getFilterForAddressSearch(search)
    else delete filter.$or

    let filter: any = {}
    let limit: number = 10
    let offset: number = 0
    let sort = "-updateTime"
    let assignedPerson: string = null
    let addresses: Address[] = []
    let selectedAddress: Address = null

    let loading = false
    let showMoreButton: boolean = false
    let deleteModalShowed: boolean = false
    let addressFormModalShowed: boolean = false

    let addNewAddress: boolean = false

    let type = addNewAddress ? "create" : "update"

    const getActionButtons: () => TableButton<"address">[] = () => [
        {
            label: "Bearbeiten",
            className: "btn-edit-address",
            svgPath: mdiFileDocumentEdit,
            onClick(address) {
                editAddressItem(address)
            },
            getTooltip(address) {
                return {
                    content: $_("address.edit"),
                }
            },
        },
        {
            label: "Löschen",
            className: "btn-delete-address",
            svgPath: mdiDelete,
            onClick(address) {
                deleteAddressModal(address)
            },
            getTooltip(address) {
                return {
                    content: $_("address.delete"),
                }
            },
        },
    ]

    const editAddressItem = async (address: Address): Promise<void> => {
        if (!address) return
        if (address.type == "person") {
            if (!address?.person?.parentCompany) {
                address.type = "company"
            } else {
                const el = addresses.find((a) => a.id === address?.person?.parentCompany)
                if (!el) {
                    await getAddress(address?.person?.parentCompany)
                        .then((resp) => {
                            if (resp.data) {
                                assignedPerson = address.id
                                address = resp.data
                            } else {
                                address.type = "company"
                            }
                        })
                        .catch((e) => {
                            address.type = "company"
                        })
                } else {
                    assignedPerson = address.id
                    address = el
                }
            }
        }
        addNewAddress = false
        type = addNewAddress ? "create" : "update"
        selectedAddress = JSON.parse(JSON.stringify(address))
        originalAddress = JSON.parse(JSON.stringify(selectedAddress))
        showAddressFormModal()
    }

    const showAddressFormModal = () => {
        deleteModalShowed = false
        addressFormModalShowed = true
    }

    const deleteAddressModal = (address: Address) => {
        selectedAddress = JSON.parse(JSON.stringify(address))
        originalAddress = JSON.parse(JSON.stringify(selectedAddress))
        deleteModalShowed = true
        addressFormModalShowed = false
    }
    const createNewAddress = () => {
        addNewAddress = true
        type = addNewAddress ? "create" : "update"
        assignedPerson = ""
        showAddressFormModal()
        doSearch()
        // @ts-ignore
        selectedAddress = JSON.parse(JSON.stringify(emptyAddress))
        selectedAddress.type = "company"
        originalAddress = JSON.parse(JSON.stringify(selectedAddress))
    }

    const addressDeleted = (e: { detail: { address: Address } }) => {
        removeAddressFromList(e.detail.address)
        doSearch()
        addressFormModalShowed = false
        deleteModalShowed = false
    }

    const removeAddressFromList = (address: Address) => {
        // Remove address from addresses list
        const addressIndex = addresses.indexOf(address)
        addresses.splice(addressIndex, 1)
        addresses = addresses

        // Consider new addresses list length and set offset minus 1
        if (offset > 0) {
            offset -= 1
        }
    }

    const loadAddresses = () => {
        loading = true
        getAddresses(filter, null, null, null, sort, false, "", true)
            .then((a) => {
                addresses = [...a.data]
                offset = a.data.length
                showMoreButton = limit === a.data.length
                addresses.forEach((a) => {
                    if (a.type === "company") return
                    if (a.type === "person" && a?.person?.parentCompany) return
                    a.type = "company"
                    updateAddress(a)
                })
            })
            .finally(() => {
                loading = false
                pageHeight = document.documentElement.scrollHeight
            })
    }

    // Search
    const doSearch = debounce(() => {
        offset = 0
        addresses = []
        loadAddresses()
    }, 300)

    $: if (sort) {
        doSearch()
    }

    const assignmentsUpdated = (e: CustomEvent) => {
        // @ts-ignore
        selectedAddress = JSON.parse(JSON.stringify(emptyAddress))
        originalAddress = JSON.parse(JSON.stringify(selectedAddress))
        addressFormModalShowed = false
        offset = 0
        addresses = []
        loadAddresses()
    }

    let addressFormElement: HTMLFormElement
    let updateFormDataByAddress: (data: Address) => Promise<void>
    let resetForm: () => void
    let parentCompany: Address
    let originalAddress = JSON.parse(JSON.stringify(selectedAddress))

    const updateLocationAndContactFieldsByAddress = (a: Address) => {
        selectedAddress.location = {}
        selectedAddress.location.street = a?.location?.street
        selectedAddress.location.houseNumber = a?.location?.houseNumber
        selectedAddress.location.supplement = a?.location?.supplement
        selectedAddress.location.postcode = a?.location?.postcode
        selectedAddress.location.city = a?.location?.city
        selectedAddress.location.country = a.location?.country
    }
    let showSuccess = false
    const updateAddressDebounce = debounce((selectedAddress: Address, parentCompany: Address) => {
        loading = true
        if (!selectedAddress) return
        if (type === "create" || type === "create-assignment") {
            createAddress(selectedAddress)
                .then((response) => {
                    newNotification({
                        class: "success",
                        html: $_("address.createSuccess"),
                        timeout: 3000,
                    })

                    closeRequest()
                    doSearch()
                })
                .catch((e) => {
                    newNotification({
                        class: "error",
                        html: $_("address.createError"),
                        timeout: 3000,
                    })
                })
                .finally(() => {
                    loading = false
                })
        } else if (type === "update" || type === "update-assignment") {
            if (parentCompany) {
                updateLocationAndContactFieldsByAddress(selectedAddress)
            }
            updateAddress(selectedAddress)
                .then(() => {
                    showSuccess = true
                    setTimeout(() => {
                        showSuccess = false
                    }, 2000)
                    doSearch()
                })
                .finally(() => {
                    loading = false
                    originalAddress = JSON.parse(JSON.stringify(selectedAddress))
                })
        }
    }, 100)
    const submitAddressFrom = (v: any) => {
        if (addressHash == hash(selectedAddress)) return
        if (addNewAddress && v === true) {
            updateAddressDebounce(selectedAddress, parentCompany)
        } else if (!addNewAddress) {
            updateAddressDebounce(selectedAddress, parentCompany)
        }
    }
    let pageHeight = document.documentElement.scrollHeight

    $: addressHash = hash(originalAddress)
    $: addressChanged = addressHash !== hash(selectedAddress)
    let refresh = false
    let askForClose = false
    function closeRequest() {
        addressFormModalShowed = false
        selectedAddress = null
        originalAddress = null
        parentCompany = null
        askForClose = false
    }
</script>

<svelte:window bind:innerWidth bind:outerHeight bind:innerHeight bind:scrollY />

<section class="addresses section_padding" data-lang="{lang}">
    <div class="container_wide">
        <h1>{$_("address.addressBook")}</h1>

        <label for="filterquery">{$_("address.freeSearch")}</label>
        <section class="address-filter-section">
            <input
                type="text"
                placeholder="{$_('address.enterSearchTerm')}"
                id="filterquery"
                bind:value="{search}"
                on:input="{doSearch}"
            />
            <!-- svelte-ignore a11y-no-static-element-interactions -->
            <span class="material-icons icon" on:click="{doSearch}" on:keydown>search</span>
        </section>

        {#if addresses.length}
            <Table
                className="address-book"
                rows="{addresses}"
                gridview="{innerWidth <= gridBreakPoint}"
                bind:sort
                on:mounted="{() => {
                    pageHeight = document.documentElement.scrollHeight
                }}"
                columns="{[
                    {
                        label: $_('address.type'),
                        field: 'type',
                        sortable: true,
                        className: 'mw-40 w-40 max-w-80',
                        component: AddressTypeField,
                        tooltipComponent: TextField,
                        getProps(address) {
                            return {
                                value: address.type,
                            }
                        },
                        getTooltipProps(address, props) {
                            return {
                                value:
                                    address.type == 'company'
                                        ? $_('address.typeTooltipCompany')
                                        : $_('address.typeTooltipContactPerson'),
                            }
                        },
                    },
                    {
                        label: $_('address.company'),
                        field: 'company.name',
                        sortable: true,
                        className: 'mw-120 w-120 angebotsnr',
                    },
                    {
                        label: $_('address.salutation'),
                        field: 'person.salutation',
                        sortable: true,
                        className: 'mw-40 w-40 max-w-80',
                    },
                    { label: $_('address.firstname'), field: 'person.firstname', sortable: true },
                    { label: $_('address.lastname'), field: 'person.lastname', sortable: true },
                    { label: $_('address.street'), field: 'location.street', sortable: true },
                    {
                        label: $_('address.houseNumber'),
                        field: 'location.houseNumber',
                        sortable: true,
                        className: 'mw-40 w-40 max-w-60',
                    },
                    { label: $_('address.supplement'), field: 'location.supplement', sortable: true },
                    {
                        label: $_('address.postcode'),
                        field: 'location.postcode',
                        sortable: true,
                        className: 'mw-80 w-80',
                    },
                    { label: $_('address.city'), field: 'location.city', sortable: true },
                    { label: $_('address.country'), field: 'location.country', sortable: true },
                    { label: $_('address.customerNumber'), field: 'customerNumber', sortable: true },
                ]}"
                buttonsColumn="{{
                    label: 'Aktionen',
                    className: 'mw-120 w-120',
                }}"
                buttons="{getActionButtons()}"
                on:click="{(e) => {
                    editAddressItem(e.detail)
                }}"
            />

            {#if loading}
                <div class="centerbutton">
                    <MiniLoader />
                </div>
            {/if}

            {#if showMoreButton && !loading}
                <!-- Load more -->
                <div class="centerbutton">
                    <button class="btn loadmore" on:click|stopPropagation="{() => loadAddresses()}">
                        {$_("general.loadMore")}
                    </button>
                </div>
            {/if}
        {/if}

        <div class="legends">
            <div class="legend">
                <div class="headline">{$_("general.legend")}</div>
                <div class="items">
                    <section>
                        <div class="item">
                            <div class="icon">
                                <Icon path="{mdiDomain}" size="24px" />
                            </div>
                            <div class="text">{$_("address.company")}</div>
                        </div>
                        <div class="item">
                            <div class="icon">
                                <Icon path="{mdiAccount}" size="24px" />
                            </div>
                            <div class="text">{$_("address.typeTooltipContactPerson")}</div>
                        </div>
                    </section>
                </div>
            </div>
        </div>

        <div
            class="main-buttons"
            class:on-bottom="{scrollY + innerHeight > pageHeight - 150}"
            use:tooltip="{{ content: $_('address.setNewAddress') }}"
        >
            <button on:click="{() => createNewAddress()}" on:keypress class="create-new-address">
                <Icon path="{mdiPlus}" size="24px" />
            </button>
        </div>
    </div>
</section>

<Modal
    show="{addressFormModalShowed}"
    size="xl"
    on:close="{() => {
        if (addressChanged) {
            askForClose = true
        } else {
            closeRequest()
        }
    }}"
>
    <div slot="header">
        {#if addNewAddress}
            {$_("address.setNewEntry")}
        {:else}
            {$_("address.editEntry")}
        {/if}
    </div>
    <div>
        {#if selectedAddress}
            {#key refresh}
                <AddressForm
                    bind:formData="{selectedAddress}"
                    bind:formElement="{addressFormElement}"
                    bind:updateFormDataByAddress
                    bind:resetForm
                    on:submit="{submitAddressFrom}"
                    readonly="{false}"
                    on:mounted="{() => {
                        originalAddress = JSON.parse(JSON.stringify(selectedAddress))
                    }}"
                    cartId="Dasisteierandomid"
                    showPriceCalcSelect="{true}"
                    disableCompanyFields="{!!selectedAddress?.person?.parentCompany &&
                        selectedAddress?.type == 'person'}"
                    saveManually="{true}"
                >
                    <div style="display: flex; justify-content: space-between;width: 100%;">
                        {#if !addNewAddress}
                            <button
                                class="btn btn-secondary"
                                on:click|preventDefault="{() => (deleteModalShowed = true)}"
                                name="delete"
                            >
                                {$_("address.deleteContact")}
                            </button>
                        {:else}
                            <div></div>
                        {/if}

                        <div style="display: flex; gap: 10px;">
                            {#if showSuccess}
                                <div style="color: green; font-size: 0.7rem; display: flex; align-items: flex-end;">
                                    {$_("address.saveData")}
                                </div>
                            {/if}
                            <div style="display: flex; flex-direction: column;  position: relative;">
                                {#if addressChanged && selectedAddress?.id}
                                    <button
                                        type="button"
                                        data-cy="cancel"
                                        use:tooltip="{{
                                            content: $_('address.resetChangesInfo'),
                                        }}"
                                        style="border: 0px; padding: 0px; background: transparent; position: absolute; top: -20px;"
                                        on:click="{() => {
                                            selectedAddress = JSON.parse(JSON.stringify(originalAddress))
                                            addressChanged = false
                                            refresh = !refresh
                                        }}"
                                    >
                                        {$_("address.reset")}
                                    </button>
                                {/if}
                                {#key addressChanged}
                                    <div
                                        use:tooltip="{{
                                            content: addressChanged
                                                ? 'invalid'
                                                : !selectedAddress.id
                                                  ? 'invalid'
                                                  : $_('address.notModified'),
                                        }}"
                                    >
                                        <button
                                            class="btn"
                                            data-cy="save"
                                            disabled="{!addressChanged}"
                                            on:click|preventDefault|stopPropagation="{() =>
                                                validateAndSaveAddress(
                                                    selectedAddress,
                                                    $login?.account?.ident?.dealerNumber,
                                                    submitAddressFrom,
                                                    addressFormElement
                                                )}"
                                        >
                                            {$_("general.save")}
                                        </button>
                                    </div>
                                {/key}
                            </div>
                        </div>
                    </div>
                </AddressForm>
            {/key}
        {/if}

        {#if selectedAddress?.type === "company" && selectedAddress?.id}
            <AssignedPersons
                bind:assignedPerson
                company="{selectedAddress}"
                on:assignment-removed="{assignmentsUpdated}"
                on:assignments-updated="{assignmentsUpdated}"
                on:refresh-addresses="{loadAddresses}"
                on:close="{() => {
                    selectedAddress = null
                }}"
            />
        {/if}
        <div class="legend">
            <div class="headline">{$_("general.legend")}</div>
            <div class="items">
                <section>
                    <div class="item">
                        <div class="icon">*</div>
                        <div class="text">{$_("general.mandatoryField")}</div>
                    </div>
                </section>
            </div>
        </div>
    </div>
</Modal>

{#if askForClose}
    <Modal
        show="{true}"
        on:close="{() => {
            askForClose = false
        }}"
        showCloseIcon="{false}"
    >
        <div slot="header">{$_("address.closeHeader")}</div>
        <div>{$_("address.unsavedChangesWarning")}</div>
        <div slot="footer">
            <button
                class="btn btn-secondary"
                on:click="{() => {
                    askForClose = false
                }}">{$_("general.no")}</button
            >
            <button
                class="btn"
                on:click="{() => {
                    closeRequest()
                }}">{$_("general.yes")}</button
            >
        </div>
    </Modal>
{/if}

<ConfirmDeleteAddressDialog
    address="{selectedAddress}"
    bind:show="{deleteModalShowed}"
    on:canceled="{() => (deleteModalShowed = false)}"
    on:address-deleted="{addressDeleted}"
/>
